import {
  LitElement,
  html,
  css,
  classMap,
  utils,
  unsafeCSS,
} from "@hh/global.js";
const { debounce } = utils;

class HHPredictiveSearch extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    openerSelector: {
      type: String,
      attribute: "opener-selector",
    },
    searchTerm: {
      type: String,
    },
    searchActive: {
      type: Boolean,
      attribute: "active",
      reflect: true,
    },
    noResults: {
      type: Boolean,
      attribute: "no-results",
      reflect: true,
    },
    initSearch: {
      type: Boolean,
      attribute: "init-search",
      reflect: true,
    },
    cachedResults: {
      type: Object,
    },
    formStyle: {
      type: String,
      attribute: "form-style",
    },
    loading: {
      type: Boolean,
    },
    isFocused: {
      type: Boolean,
    },
  };

  constructor() {
    super();

    this.debounceSearchInput = debounce(this.onSearchInput.bind(this), 500);

    this.cachedResults = {};

    this.classes = {
      hidden: "hidden",
    };

    this.loading = false;
    this.isFocused = false;

    this.minChars = 1;

    // https://shopify.dev/docs/api/ajax/reference/predictive-search
    this.resourceTypes = ["product"];

    this.searchFields = [
      "body",
      "product_type",
      "tag",
      "title",
      "variants.barcode",
      "variants.sku",
      "variants.title",
      "vendor",
    ];
  }

  get opener() {
    return document.querySelector(this.openerSelector) ?? null;
  }

  get form() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-form"]')
        ?.assignedNodes()[0] ?? null
    );
  }

  get searchInput() {
    return this.form?.querySelector('input[type="search"]') ?? null;
  }

  get clearButton() {
    return this.form?.querySelector(".search--clear") ?? null;
  }

  get searchResults() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-results"]')
        ?.assignedNodes()[0] ?? null
    );
  }

  get liveRegionFilter() {
    return this.querySelector("[data-live-region]") ?? null;
  }

  get popularSearches() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-inactive"]')
        ?.assignedNodes()[0] ?? null
    );
  }

  get quickSearchButtons() {
    return [...this.querySelectorAll("[data-search-term]")];
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("modalClosed", (e) => {
      if (e.detail.placement === "mobile-menu") {
        this.onClear();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener("modalClosed", (e) => {
      if (e.detail.placement === "mobile-menu") {
        this.onClear();
      }
    });
  }

  firstUpdated() {
    this.searchInput.addEventListener("input", this.onSearchInput.bind(this));
    this.searchInput.value = this.searchTerm ?? "";
    this.clearButton.addEventListener("click", this.onClear.bind(this));
    this.searchInput?.addEventListener("focus", () => {
      this.isFocused = true;
    });
    this.quickSearchButtons.forEach((button) => {
      button.addEventListener("click", this.onQuickSearch.bind(this));
    });
  }

  updated(changedProperties) {
    if (changedProperties.has("searchTerm")) {
      this.searchChange(this.searchTerm);
    }
  }

  onSearchInput(e) {
    if (!this.searchInput) return;

    const { value } = e.target;

    if (value.length === 0) {
      this.initSearch = false;
      if (this.searchResults) this.searchResults.innerHTML = "";
      this.popularSearches?.classList.remove(this.classes.hidden);
      this.refreshFocusTrap();
    }

    this.searchTerm = value;
  }

  onClear() {
    this.searchInput.value = "";
    this.searchTerm = "";
    this.searchActive = false;
    if (this.searchResults) this.searchResults.innerHTML = "";
    this.liveRegionFilter.textContent = "";
    this.isFocused = false;
    this.refreshFocusTrap();
    this.popularSearches?.classList.remove(this.classes.hidden);
    // Allow scrolling if there are no active search results
    document.documentElement.classList.remove("overflow-hidden");
  }

  onQuickSearch(e) {
    this.isFocused = true;
    e.preventDefault();
    const value = e.currentTarget.dataset.searchTerm;

    if (!this.searchInput) return;

    this.searchInput.value = value;
    const inputEvent = new Event("input", { bubbles: true, composed: true });
    this.searchInput.dispatchEvent(inputEvent);
  }

  searchChange(query) {
    const newSearchTerm = query.trim();
    if (newSearchTerm.length < this.minChars) {
      this.searchActive = false;
      this.clearButton.classList.add(this.classes.hidden);
      this.popularSearches?.classList.remove(this.classes.hidden);
      return;
    }

    this.searchActive = true;
    this.getSearchResults(newSearchTerm);
    this.clearButton.classList.remove(this.classes.hidden);
    this.popularSearches?.classList.add(this.classes.hidden);
  }

  async getSearchResults(query) {
    const queryKey = query.replaceAll(" ", "-").toLowerCase();

    if (this.cachedResults?.[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.loading = true;
    const searchEndpoint = window.routes.predictive_search_url;

    const params = {
      q: query,
      "resources[type]": this.resourceTypes.join(","),
      "resources[options][fields]": this.searchFields.join(","),
      section_id: "predictive-search",
    };

    const paramsString = new URLSearchParams(params).toString();
    const searchUrl = `${searchEndpoint}?${paramsString}`;

    try {
      const res = await fetch(searchUrl);
      if (!res.ok) throw new Error(res.status);

      const text = await res.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-predictive-search");

      this.cachedResults[queryKey] = resultsMarkup;
      this.loading = false;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      this.loading = false;
      this.liveRegionFilter.textContent = "<p>No results available</p>";

      console.error("An error occurred in the predictive search request: ", {
        error,
        query,
        searchUrl,
      });
    }
  }

  renderSearchResults(resultsMarkup) {
    this.noResults = resultsMarkup.querySelector(".js-no-results") !== null;

    // Get custom products from data attribute if available
    const customProductsData = this.getAttribute("data-custom-products");
    let customProducts = [];
    
    if (customProductsData) {
      try {
        customProducts = JSON.parse(customProductsData);
        if (!Array.isArray(customProducts)) {
          customProducts = [];
        }
      } catch (error) {
        console.warn("Could not parse custom products data:", error);
        customProducts = [];
      }
    }

    // If we have custom products, integrate them into the grid
    if (customProducts.length > 0) {
      const combinedHTML = this.integrateCustomProducts(resultsMarkup, customProducts);
      this.searchResults.innerHTML = combinedHTML;
      
      // Update noResults status - if we have custom products, we have results
      this.noResults = false;
    } else {
      // No custom products, use regular search results
      this.searchResults.innerHTML = resultsMarkup.innerHTML;
    }

    if (this.noResults) {
      this.liveRegionFilter.textContent = "No results available";
    } else if (resultsMarkup || customProducts.length > 0) {
      const totalResults = this.searchResults.querySelectorAll(".predictive-search-products").length;
      this.liveRegionFilter.textContent = `${totalResults} results available`;
    }

    this.refreshFocusTrap();
  }

  integrateCustomProducts(resultsMarkup, customProducts) {
    // Parse the search results HTML to extract individual product cards
    const searchResultsContainer = resultsMarkup.querySelector('.predictive-search__results');
    
    // Get all existing search result products (empty if no results)
    const existingProducts = searchResultsContainer ? 
      Array.from(searchResultsContainer.querySelectorAll('.predictive-search-products')) : [];
    
    // Calculate how many search results to keep (4 total - custom products count)
    const maxResults = 4;
    const customProductsCount = Math.min(customProducts.length, maxResults);
    const searchResultsToKeep = Math.max(0, maxResults - customProductsCount);
    
    // Keep only the first N search results
    const keptSearchResults = existingProducts.slice(0, searchResultsToKeep);
    
    // Generate HTML for custom products
    const customProductsHTML = customProducts.slice(0, customProductsCount).map(product => {
      return this.renderSingleCustomProduct(product);
    }).join('');
    
    // Handle different scenarios for the heading
    let headingHTML = '';
    if (searchResultsContainer) {
      const heading = searchResultsContainer.querySelector('.predictive-search-heading');
      if (heading && keptSearchResults.length > 0) {
        // Keep original heading if we have search results
        headingHTML = heading.outerHTML;
      } else if (customProductsHTML) {
        // Create a heading for custom products only
        headingHTML = `
          <div class='predictive-search-heading'>
            <h2 id='predictive-search-results'>
              Recommended Products
              <span>(${customProductsCount})</span>
            </h2>
          </div>
        `;
      }
    } else if (customProductsHTML) {
      // No search results container, create one for custom products
      headingHTML = `
        <div class='predictive-search-heading'>
          <h2 id='predictive-search-results'>
            Recommended Products
            <span>(${customProductsCount})</span>
          </h2>
        </div>
      `;
    }
    
    const keptResultsHTML = keptSearchResults.map(el => el.outerHTML).join('');
    
    return `
      <div role='dialog' aria-modal='true' class='predictive-search__results'>
        ${headingHTML}
        ${keptResultsHTML}
        ${customProductsHTML}
      </div>
    `;
  }

  renderSingleCustomProduct(product) {
    const productUrl = product.url || (product.handle ? `/products/${product.handle}` : "#");
    const productTitle = product.title || "Product";
    
    // Get metafield data from the separate data attribute
    const metafieldsData = this.getAttribute("data-custom-metafields");
    let isUnshoppable = false;
    let hasExternalUrls = false;
    
    if (metafieldsData) {
      // Parse the metafields data (format: "id1:isUnshoppable:hasExternalUrls,id2:isUnshoppable:hasExternalUrls")
      const metafields = metafieldsData.split(',').reduce((acc, item) => {
        const [id, isUnshoppable, hasExternalUrls] = item.split(':');
        acc[id] = {
          isUnshoppable: isUnshoppable === 'true',
          hasExternalUrls: hasExternalUrls === 'true'
        };
        return acc;
      }, {});
      
      const productMetafields = metafields[product.id] || { isUnshoppable: false, hasExternalUrls: false };
      isUnshoppable = productMetafields.isUnshoppable;
      hasExternalUrls = productMetafields.hasExternalUrls;
    }
    
    // Don't hide unshoppable products, just modify their button behavior
    
    return `<div class='predictive-search-products'>
      <hh-broadcast on="productAdded" event="modal" class="block ht1">
        <hh-product-form show-mini-cart>
          <form class='product-card-form form' action='/cart/add' method='post' enctype='multipart/form-data' novalidate='novalidate' data-type='add-to-cart-form' data-product-id='${product.id}'>
            <input name="quantity" type="hidden" value="1">
            <input type="hidden" class="js-product-form-input" name="id" value="${product.variants?.[0]?.id || product.id}">
            
            <div class="product-card__wrapper">
              <a class='js-anchor relative block' href='${productUrl}'>
                <div class='product-card__media' style='aspect-ratio: 1'>
                  ${product.featured_image ? `<img src="${product.featured_image}" alt="${productTitle}" class="product-media" style="width: 100%; height: 100%; object-fit: cover;">` : ''}
                </div>
                <div class='product-card__badge-wrapper flex'>
                  <div class='product-card__badge utility'>
                    Recommended
                  </div>
                </div>
              </a>
              <div class="product-card__info-wrapper">
                <div class="product-card__details">
                  <div class="product-card__info">
                    <a href="${productUrl}" class="h6 product-card--link">
                      ${productTitle}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div class="product-card__info--bottom">
              <div class="product-card__info view-desktop">
                <div class="price body-sm">
                  ${product.price ? `<span class="price-item price-item--regular">$${(product.price / 100).toFixed(2)}</span>` : ''}
                </div>
              </div>
              <div class="product-card__atc relative flex w1">
                <hh-button-wrapper width="full" theme="secondary">
                  ${hasExternalUrls ? `
                    <a class="button product-card__button js-product-form-button full-width" href="${productUrl}">
                      See Options
                    </a>
                  ` : `
                    <button type="submit" name="add" class="product-card__button js-product-form-button full-width" ${!product.available ? 'disabled' : ''} style="${isUnshoppable ? 'visibility: hidden;' : ''}">
                      <span class="product-card__available">${product.available !== false ? 'Add to cart' : 'Sold out'}</span>
                    </button>
                  `}
                </hh-button-wrapper>
              </div>
            </div>
          </form>
        </hh-product-form>
      </hh-broadcast>
    </div>`;
  }

  renderCustomProductsHTML(customProducts) {
    if (!Array.isArray(customProducts) || customProducts.length === 0) {
      return "";
    }

    // For the old method that renders all at once (not used in new integration)
    return customProducts
      .map((product) => {
        return this.renderSingleCustomProduct(product);
      })
      .join("");
  }

  refreshFocusTrap(timeout = 0) {
    setTimeout(() => {
      this.dispatchEvent(
        new CustomEvent("focus-trap:refresh", { bubbles: true, composed: true })
      );
    }, timeout);
  }

  render() {
    return html`
      <div
        class="predictive-search-wrapper ${classMap({
          pinned: this.isFocused || this.searchActive,
        })}"
      >
        <div class="search-form-wrapper">
          <slot name="search-form"></slot>
        </div>

        <div
          class="search-container ${classMap({
            active: this.searchActive,
            visible: this.isFocused || this.searchActive,
          })}"
        >
          ${this.loading
            ? html`<div class="search--loader">
                <slot name="loader"></slot>
              </div>`
            : ""}
          <slot name="search-inactive"></slot>
          <div
            class=${classMap({
              "search-results-container": true,
              active: this.searchActive,
            })}
            aria-hidden="${!this.searchActive}"
            ?inert=${!this.searchActive}
          >
            <div class="search-results-inner">
              <slot name="search-results"></slot>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .search-container {
      position: relative;
      background-color: var(--hh-container-background-color, #fff);
      overflow-y: scroll;
      display: none;
      height: 100%;
      max-height: calc(
        100dvh - ${unsafeCSS("var(--hh-header-height)")} -
          ${unsafeCSS("var(--hh-search-height)")}
      );
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }

    .search-container ::-webkit-scrollbar {
      display: none;
    }

    .search--loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      margin-bottom: 1rem;
    }

    .search-container.visible {
      display: flex;
      flex-direction: column;
    }

    @media (min-width: 750px) {
      .search-container.visible {
        display: flex;
        flex-direction: column;
      }
      .search-container {
        height: calc(100vh - 187px);
        padding: 0;
        max-height: none;
      }
    }

    @media (min-width: 990px) {
      .search-form-wrapper {
        display: flex;
      }

      .search-container {
        padding: 30px 0;
      }
    }

    .search-results-container {
      display: none;
    }

    .search-results-container.active {
      display: block;
      width: 100%;
    }

    @media (max-width: 989px) {
      .predictive-search-wrapper.pinned {
        position: fixed;
        background-color: var(--hh-container-background-color, #fff);
        top: 60px;
        height: calc(100lvh - 60px);
        width: 100%;
        z-index: 10;
      }
    }
  `;
}

customElements.define("hh-predictive-search", HHPredictiveSearch);
